import { api, withFallback } from "./api/client";
import { mockOpportunities } from "@/lib/mock-intelligence";

import type {
  OpportunityCard,
  OpportunityFilters,
  OpportunityInsights,
  OpportunityScore,
  Id,
} from "@/types";

/** Respuesta cruda del BFF: licitación real + scoring + trazabilidad. */
interface ApiOpportunity {
  licitacion: Record<string, unknown>;
  scoring: OpportunityScore;
  origen: "ia" | "deterministico";
  reglasEvaluadas: {
    id: string;
    titulo: string;
    prioridad: string;
    cumple: boolean;
    detalle: string;
  }[];
  evaluadoAt: string | null;
  veredictoTexto: string;
  recomendaciones: string[];
  agenteVersion: string;
  modelo: string;
}

/** Análisis narrativo de un agente IA sobre la licitación. */
export interface AgentAnalysis {
  agenteId: string;
  nombre: string;
  descripcion: string;
  icono: string;
  categoria: string;
  activo: boolean;
  resumen: string | null;
  contenido: string | null;
  modelo: string | null;
  /** manual | auto */
  origenEjecucion: string | null;
  creadoAt: string | null;
}

export interface OpportunityCardApi extends OpportunityCard {
  origen: "ia" | "deterministico";
  reglasEvaluadas: ApiOpportunity["reglasEvaluadas"];
  evaluadoAt: string | null;
  veredictoTexto: string;
  recomendaciones: string[];
  agenteVersion: string;
  modelo: string;
}

const str = (v: unknown, def = "") => (typeof v === "string" && v ? v : def);

/** Adapta la licitación real de la BD al shape que consume la UI de oportunidades. */
function toCard(item: ApiOpportunity): OpportunityCardApi {
  const l = item.licitacion;
  const categorias = (l.categoriaUnspsc as string[]) ?? [];
  return {
    licitacion: {
      id: str(l.id),
      codigo: str(l.codigo),
      nombre: str(l.nombre),
      organismo: str(l.organismo),
      categoria: categorias[0] ?? "Sin categoría",
      region: str(l.region, "Sin región"),
      montoEstimado: Number(l.montoEstimado ?? 0),
      moneda: (str(l.moneda, "CLP") as "CLP" | "UF" | "UTM") ?? "CLP",
      fechaPublicacion: str(l.fechaPublicacion),
      fechaCierre: str(l.fechaCierre),
      estado: "detectada",
      prioridad: item.scoring.score >= 70 ? "alta" : item.scoring.score >= 40 ? "media" : "baja",
      scoreIA: item.scoring.score,
      responsable: "",
      modalidad: str(l.tipoMP, "Licitación Pública") as never,
      riesgo: item.scoring.riesgo,
      documentos: 0,
    },
    scoring: item.scoring,
    origen: item.origen,
    reglasEvaluadas: item.reglasEvaluadas ?? [],
    evaluadoAt: item.evaluadoAt,
    veredictoTexto: item.veredictoTexto ?? "",
    recomendaciones: item.recomendaciones ?? [],
    agenteVersion: item.agenteVersion ?? "",
    modelo: item.modelo ?? "",
  };
}

function applyFilters(items: OpportunityCard[], f?: OpportunityFilters): OpportunityCard[] {
  if (!f) return items;
  return items.filter((o) => {
    if (f.minScore != null && o.scoring.score < f.minScore) return false;
    if (f.riesgo?.length && !f.riesgo.includes(o.scoring.riesgo)) return false;
    if (f.recommendation?.length && !f.recommendation.includes(o.scoring.recommendation))
      return false;
    if (f.categoria?.length && !f.categoria.includes(o.licitacion.categoria)) return false;
    return true;
  });
}

/** Respuesta paginada del backend con las métricas del embudo. */
export interface OpportunityPage {
  items: OpportunityCardApi[];
  total: number;
  page: number;
  pageSize: number;
  embudo: {
    vigentes: number;
    prefiltradas: number;
    sobreUmbral: number;
    evaluadasIa: number;
    umbral: number;
  };
  estadoProceso: {
    procesando: boolean;
    total: number;
    listas: number;
    ultimoProcesoAt: string | null;
  };
}

interface ApiPage {
  items: ApiOpportunity[];
  total: number;
  page: number;
  pageSize: number;
  embudo: OpportunityPage["embudo"];
  estadoProceso: OpportunityPage["estadoProceso"];
}

export const opportunitiesService = {
  recommended: (filters?: OpportunityFilters) =>
    withFallback<OpportunityCard[]>(
      async () => {
        const { data } = await api.get<ApiPage>("/oportunidades");
        return applyFilters((data.items ?? []).map(toCard), filters);
      },
      () => applyFilters(mockOpportunities, filters),
    ),

  /**
   * Listado real desde el backend: sin fallback a mocks para que los IDs
   * coincidan siempre con el detalle (`/oportunidades/:id`).
   */
  list: async (
    filters?: OpportunityFilters,
    page = 1,
    pageSize = 50,
  ): Promise<OpportunityPage> => {
    const { data } = await api.get<ApiPage>("/oportunidades", {
      params: { page, pageSize, ...(filters?.minScore != null ? { minScore: filters.minScore } : {}) },
    });
    return {
      ...data,
      items: applyFilters((data.items ?? []).map(toCard), filters) as OpportunityCardApi[],
    };
  },

  /** Re-procesa el embudo determinístico del tenant. */
  procesar: async (modo: "full" | "incremental" = "incremental") =>
    (
      await api.post<{ procesadas: number; modo: string; yaEnCurso?: boolean }>(
        "/oportunidades/procesar",
        { modo },
      )
    ).data,

  /** Detalle real desde el backend: sin fallback para no enmascarar errores. */
  byId: async (id: Id): Promise<OpportunityCardApi> =>
    toCard((await api.get<ApiOpportunity>(`/oportunidades/${id}`)).data),


  /** Agentes IA disponibles y análisis ya generados. */
  analisis: async (id: Id) =>
    (await api.get<AgentAnalysis[]>(`/oportunidades/${id}/analisis`)).data,

  /** Ejecuta un agente IA sobre la licitación. */
  analizar: async (id: Id, agenteId: string) =>
    (await api.post<AgentAnalysis>(`/oportunidades/${id}/analisis`, { agenteId })).data,

  /** Dispara la evaluación con el motor IA configurado por el tenant. */
  evaluar: async (licitacionId?: string) =>
    (
      await api.post<{ evaluadas: number; errores: number; error?: string }>(
        "/oportunidades/evaluar",
        { licitacionId: licitacionId ?? null },
      )
    ).data,


  insights: () =>
    withFallback<OpportunityInsights>(
      async () => (await api.get<OpportunityInsights>("/oportunidades/insights")).data,
      () => {
        const recomendadas = mockOpportunities.filter(
          (o) => o.scoring.recommendation === "postular",
        ).length;
        const total = mockOpportunities.length;
        const avg = Math.round(
          mockOpportunities.reduce((a, o) => a + o.scoring.score, 0) / Math.max(total, 1),
        );
        const monto = mockOpportunities
          .filter((o) => o.scoring.recommendation === "postular")
          .reduce((a, o) => a + o.licitacion.montoEstimado, 0);
        const organismos = Array.from(
          new Set(
            mockOpportunities
              .filter((o) => o.scoring.score >= 70)
              .map((o) => o.licitacion.organismo),
          ),
        ).slice(0, 5);
        const categorias = Array.from(
          new Set(
            mockOpportunities
              .filter((o) => o.scoring.recommendation === "postular")
              .map((o) => o.licitacion.categoria),
          ),
        ).slice(0, 5);
        return {
          totalAnalizadas: total,
          recomendadas,
          scorePromedio: avg,
          montoOportunidad: monto,
          organismosFavorables: organismos,
          categoriasGanadoras: categorias,
        };
      },
    ),
};
